import { useCallback } from 'react'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { rem } from 'polished'
import { fillArray } from 'lib/array/arrayUtils'
import styled from 'styled-components'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useCruiseSearchFacets, { FACET_TYPES } from 'hooks/Cruise/useCruiseSearchFacets'
import LayoutContainer from 'components/Common/LayoutContainer'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Heading from 'LuxKit/Typography/Heading'
import TrackedCardCarousel from 'components/Common/TrackedCardCarousel/TrackedCardCarousel'
import TrackingContext from 'contexts/trackingContext'
import Group from 'components/utils/Group'
import CruiseLinesSectionItem from './CruiseLinesSectionItem'

const italicFormatter = (chunks: React.ReactNode) => <i>{chunks}</i>

const ContentSkeleton = styled(LoadingBox)`
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.M};
  height: ${rem(100)};
`

function CruisePageCruiseLinesCarousel() {
  const isMobileScreen = useScreenSizeOnly('mobile')
  const itemsToShow = isMobileScreen ? 3 : 7
  const [cruiseLineFacets, fetching] = useCruiseSearchFacets({
    facetTypes: [FACET_TYPES.CRUISE_LINES],
    offerSource: 'cruises',
  })

  const getItemLink = useCallback((cruiseLine: App.CruiseSearchFacet) => {
    if (cruiseLine.slug) {
      return `/cruise-line/${cruiseLine.slug}`
    }

    if (cruiseLine.value) {
      return `/search/cruises?cruiseLines=${encodeURIComponent(cruiseLine.value)}`
    }
  }, [])

  if (!cruiseLineFacets?.length) {
    return null
  }

  return (
    <TrackingContext.Provider
      value={{
        category: 'cruise_lines',
        label: 'Cruise Page Lines',
        listId: 'cruise-lines-carousel',
        listName: 'Cruise Page Lines',
      }}
    >
      <Group direction="vertical" gap={32}>
        <LayoutContainer>
          <Heading variant="heading3">
            <FormattedMessage
              id="ecGlRd9CQJlpZ-SVS2UH"
              defaultMessage="Explore by featured <i>cruise lines</i>"
              description="Section heading for featured cruise lines on cruise landing page"
              values={{ i: italicFormatter }}
            />
          </Heading>
        </LayoutContainer>

        <LayoutContainer>
          <Group direction="vertical" gap={8}>
            <TrackedCardCarousel pageSize={itemsToShow} gap={12}>
              {cruiseLineFacets?.map((facet) => {
                return <CruiseLinesSectionItem
                  key={facet.imageId}
                  cruiseLine={facet}
                  to={getItemLink(facet)}
                  isQuickFilter
                />
              })}
            </TrackedCardCarousel>

            {fetching &&
              <Group direction="horizontal" gap={16}>
                {fillArray(itemsToShow).map((id) => <ContentSkeleton key={id} />)}
              </Group>}
          </Group>
        </LayoutContainer>
      </Group>
    </TrackingContext.Provider>
  )
}

export default CruisePageCruiseLinesCarousel
